// One loaded editor snapshot. Only this queue's confirmed writes advance it;
// presence/reconnect/foreign GETs must never stamp a new version onto a draft.
export function createReportMutationQueue({ onSaved, onError, onPendingChange } = {}) {
  let version = null, blocked = null, stopped = false, generation = 0, tail = Promise.resolve();
  let writes = 0, pending = 0;
  function fail(error) {
    if (blocked || stopped) return;
    blocked = error;
    if (!stopped) onError?.(error);
  }
  return {
    fail,
    refreshToken: () => `${generation}:${writes}`,
    replaceCleanSnapshot(next, token) {
      if (stopped || blocked || pending || token !== `${generation}:${writes}` || !next) throw new Error("Editor changed while refreshing");
      version = next;
    },
    adoptSnapshot(next) {
      if (blocked) throw blocked;
      if (stopped) return;
      if (version != null && next !== version) {
        const error = { problem: { code: "SNAPSHOT_CHANGED", kind: "conflict", outcome: "not_applied", recovery: "reload", message: "Rapporten ble endret under lasting. Kladden er beholdt. Hent siste versjon før du fortsetter." } };
        fail(error);
        throw error;
      }
      if (!blocked && !stopped) version = next;
    },
    start() { stopped = false; return ++generation; },
    stop(expectedGeneration) { if (expectedGeneration == null || expectedGeneration === generation) stopped = true; },
    run(persist) {
      writes++;
      onPendingChange?.(++pending);
      const result = tail.then(async () => {
        if (stopped) throw new Error("Editor closed");
        if (blocked) throw blocked;
        try {
          // Explicitly blank fails closed if an old API omits snapshot metadata.
          const saved = await persist(version ?? "");
          if (saved?.errors || saved?.problem) throw saved;
          const next = saved?.report_edit_version ?? saved?.data?.report_edit_version;
          if (!next) throw { errors: ["Lagringsresultatet mangler versjon. Hent siste versjon før du fortsetter."], outcome: "unknown" };
          version = next;
          if (!stopped) onSaved?.(saved, version);
          return saved;
        } catch (error) {
          fail(error);
          throw error;
        }
      });
      const settled = result.finally(() => { onPendingChange?.(--pending); });
      tail = settled.catch(() => {});
      return settled;
    },
  };
}
